import React, { useState, useEffect } from 'react';
import { courseService } from '../../services/courseService';

const CourseForm = ({ onSubmit, onCancel, initialData = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    level: '',
    section: '',
    year: new Date().getFullYear(),
    students: 0,
    headTeacherId: '',
    headTeacher: '',
    status: 'activo'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [levels, setLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [years, setYears] = useState([]);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    // Si hay datos iniciales (modo edición), cargarlos
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        level: initialData.level || '',
        section: initialData.section || '',
        year: initialData.year || new Date().getFullYear(),
        students: initialData.students || 0,
        headTeacherId: initialData.headTeacherId || '',
        headTeacher: initialData.headTeacher || '',
        status: initialData.status || 'activo'
      });
    }
  }, [initialData]);

  // Actualizar nombre del curso cuando cambian level o section
  useEffect(() => {
    if (formData.level && formData.section) {
      setFormData(prev => ({
        ...prev,
        name: `${formData.level} ${formData.section}`
      }));
    }
  }, [formData.level, formData.section]);

  const loadFormData = async () => {
    setLevels(courseService.getLevels());
    setSections(courseService.getSections());
    setYears(courseService.getYears());

    const teachersResponse = await courseService.getTeachers();
    if (teachersResponse.success) {
      setTeachers(teachersResponse.data);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Si se cambia el profesor jefe, actualizar también el nombre
    if (name === 'headTeacherId') {
      const teacher = teachers.find(t => t.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        headTeacherId: value,
        headTeacher: teacher ? teacher.name : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Limpiar error del campo al escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar nivel
    if (!formData.level) {
      newErrors.level = 'Debes seleccionar un nivel';
    }

    // Validar sección
    if (!formData.section) {
      newErrors.section = 'Debes seleccionar una sección';
    }

    // Validar año
    if (!formData.year) {
      newErrors.year = 'El año es requerido';
    }

    // Validar profesor jefe
    if (!formData.headTeacherId) {
      newErrors.headTeacherId = 'Debes seleccionar un profesor jefe';
    }

    // Validar número de estudiantes
    if (formData.students < 0) {
      newErrors.students = 'El número de estudiantes no puede ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar datos para enviar
      const dataToSubmit = {
        name: formData.name,
        level: formData.level,
        section: formData.section,
        year: parseInt(formData.year),
        students: parseInt(formData.students) || 0,
        headTeacherId: parseInt(formData.headTeacherId),
        headTeacher: formData.headTeacher,
        status: formData.status
      };

      // Si es modo edición, mantener las materias
      if (isEditMode && initialData?.subjects) {
        dataToSubmit.subjects = initialData.subjects;
      }

      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">
          {isEditMode ? 'Editar Curso' : 'Nuevo Curso'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {isEditMode
            ? 'Actualiza la información del curso'
            : 'Completa los datos para crear un nuevo curso'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Información del Curso */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Información del Curso
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Nivel */}
            <div>
              <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
                Nivel *
              </label>
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.level ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar nivel</option>
                {levels.map(level => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
              {errors.level && <p className="text-red-500 text-xs mt-1">{errors.level}</p>}
            </div>

            {/* Sección */}
            <div>
              <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-1">
                Sección *
              </label>
              <select
                id="section"
                name="section"
                value={formData.section}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.section ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar sección</option>
                {sections.map(section => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
              {errors.section && <p className="text-red-500 text-xs mt-1">{errors.section}</p>}
            </div>

            {/* Año */}
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                Año *
              </label>
              <select
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
            </div>
          </div>

          {/* Nombre del curso (generado automáticamente) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del Curso
            </label>
            <input
              type="text"
              value={formData.name}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              placeholder="Se genera automáticamente"
            />
            <p className="text-xs text-gray-500 mt-1">
              El nombre se genera automáticamente según el nivel y sección
            </p>
          </div>
        </div>

        {/* Información Adicional */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Información Adicional
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Profesor Jefe */}
            <div>
              <label htmlFor="headTeacherId" className="block text-sm font-medium text-gray-700 mb-1">
                Profesor Jefe *
              </label>
              <select
                id="headTeacherId"
                name="headTeacherId"
                value={formData.headTeacherId}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.headTeacherId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar profesor</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.name}
                  </option>
                ))}
              </select>
              {errors.headTeacherId && (
                <p className="text-red-500 text-xs mt-1">{errors.headTeacherId}</p>
              )}
            </div>

            {/* Número de estudiantes */}
            <div>
              <label htmlFor="students" className="block text-sm font-medium text-gray-700 mb-1">
                Número de Estudiantes
              </label>
              <input
                type="number"
                id="students"
                name="students"
                value={formData.students}
                onChange={handleChange}
                min="0"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.students ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.students && <p className="text-red-500 text-xs mt-1">{errors.students}</p>}
            </div>

            {/* Estado */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Estado *
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Nota informativa */}
        {!isEditMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Gestión de materias</p>
                <p>
                  Podrás agregar y configurar las materias del curso después de crearlo,
                  en la vista de detalle del curso.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Guardando...' : isEditMode ? 'Actualizar' : 'Crear Curso'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourseForm;
